import React, { Suspense, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import { useTranslation } from 'react-i18next';
import { STATUS } from '../../../../helpers/constants';
import {
  getAllStockTransactionForCsv,
  getFilterStockTransaction,
} from '../../../../store/requests/stock';
import {
  Button,
  Col,
  ConfigProvider,
  notification,
  Row,
  Table,
  Tooltip,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import trTR from 'antd/es/locale/tr_TR';
import moment from 'moment';

const StockTransactionFilterForm = React.lazy(
  () => import('./StockTransactionFilterForm'),
);

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 20,
  },
};
const StockTransactions = ({ route }) => {
  const history = useHistory();
  const location = useLocation();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [filters, setFilters] = useState({});
  const [transactionData, setTransactionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stockTrackingData, setStockTrackingData] = useState(null);
  const [excelDownloadLoading, setExcelDownloadLoading] = useState(false);
  const [tableParams, setTableParams] = useState(initialPageParams);

  useEffect(() => {
    if (location?.state?.stock) {
      setStockTrackingData(location?.state.stock);
      clearHistoryState();
    }
  }, []);

  const clearHistoryState = () => {
    const updatedState = {
      ...history.location.state,
      stock: null,
    };

    history.replace({
      ...history.location,
      state: updatedState,
    });
  };

  const fetchData = (tableParamsValue, filterValue) => {
    const queryString = getQueryString(tableParamsValue, filterValue);
    getStockTransactions(queryString);
  };

  const getQueryString = (tableParamsValue, filterValue) => {
    let queryString = '';

    setFilters(filterValue);

    if (Object.values(filterValue).some((item) => !!item)) {
      queryString += Object.entries(filterValue)
        .filter(
          ([item, value]) =>
            value != null && value !== undefined && filterValue[item],
        )
        .map(
          ([item, value]) =>
            `${item}=${typeof value === 'string' ? value.trim() : value}`,
        )
        .join('&');
    }

    queryString = `${queryString}&page=${tableParamsValue.pagination.current - 1}&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue?.column) {
      // queryString += `&sort=${tableParamsValue.column.dataIndex},${ORDER_TYPES[tableParamsValue.order]}`;
    }

    return queryString;
  };

  function getStockTransactions(queryString) {
    if (!loading) {
      setLoading(true);
      getFilterStockTransaction(queryString)
        .then((stock) => {
          const { content, page } = stock;
          setTransactionData(content);
          setTableParams({
            pagination: {
              total: page.totalElements,
              current: page.number + 1,
              pageSize: page.size,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const handleTableChange = (pagination, sorter) => {
    fetchData(
      {
        pagination,
        ...sorter,
      },
      filters,
    );
  };

  const columns = [
    {
      title: t('Offer ID'),
      dataIndex: 'offerId',
      showSorterTooltip: false,
      key: 'offerId',
    },
    {
      title: t('ERP Kodu'),
      dataIndex: 'erpCode',
      showSorterTooltip: false,
      key: 'erpCode',
    },
    {
      title: t('productName'),
      dataIndex: 'productName',
      showSorterTooltip: false,
      key: 'productName',
    },
    {
      title: t('transactionDate'),
      dataIndex: 'createdAt',
      showSorterTooltip: false,
      render: (text, record) =>
        moment(record?.createdAt).format('DD.MM.YYYY / HH:mm'),
      key: 'createdAt',
    },
    {
      title: t('sellerSource'),
      dataIndex: 'sellerSourceLabel',
      showSorterTooltip: false,
      key: 'sellerSourceLabel',
    },
    {
      title: t('transactionType'),
      dataIndex: 'transactionTypeLabel',
      showSorterTooltip: false,
      key: 'transactionTypeLabel',
    },
    {
      title: t('transactionSource'),
      dataIndex: 'transactionSourceLabel',
      showSorterTooltip: false,
      key: 'transactionSourceLabel',
    },
    {
      title: t('operationCode'),
      dataIndex: 'referenceId',
      showSorterTooltip: false,
      key: 'referenceId',
    },
    {
      title: t('transactionQuantity'),
      dataIndex: 'transactionQuantity',
      showSorterTooltip: false,
      key: 'transactionQuantity',
    },
    {
      title: t('stockTransactionQuantity'),
      dataIndex: 'quantity',
      showSorterTooltip: false,
      key: 'quantity',
    },
    {
      title: t('currentQuantity'),
      dataIndex: 'currentQuantity',
      showSorterTooltip: false,
      key: 'currentQuantity',
    },
    {
      title: t('currentFreeQuantity'),
      dataIndex: 'currentFreeQuantity',
      showSorterTooltip: false,
      key: 'currentFreeQuantity',
    },
  ];

  const getAllStockTracking = async () => {
    setExcelDownloadLoading(true);
    const keyMapping = {
      'store-id': 'storeId',
      'start-date': 'startDate',
      'end-date': 'endDate',
      'offer-id': 'offerId',
      'external-sku-id': 'externalSkuId',
      'reference-id': 'referenceId',
    };
    const payload = Object.fromEntries(
      Object.entries(filters)
        .filter(([key, value]) => value !== undefined)
        .map(([key, value]) => [
          keyMapping[key] || key,
          key === 'store-id' ? String(value) : value,
        ]),
    );

    payload.queryString = `page=0&size=2000`;

    getAllStockTransactionForCsv(payload)
      .then((res) => {
        notificationToast(STATUS.SUCCESS);
      })
      .finally(() => setExcelDownloadLoading(false));
  };

  const notificationToast = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('reportLinkSentMailMessage'),
    });
  };

  const actions = [
    {
      isCrudEnabled: true,
      id: 'downloadButton',
      type: '',
      title: t('download'),
      customContent: (
        <>
          <Button
            shape="round"
            onClick={getAllStockTracking}
            icon={<DownloadOutlined />}
            loading={excelDownloadLoading.loading}
            style={{ marginBlock: 5 }}
            disabled={!transactionData.length > 0}
          />
        </>
      ),
      disabled: false,
      hasDropdown: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBlock: 5 }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  return (
    <>
      <Suspense fallback={<></>}>
        <StockTransactionFilterForm
          handlerFilter={(filterValue) => {
            fetchData(initialPageParams, filterValue);
          }}
          loading={loading}
          stockTrackingData={stockTrackingData}
          clearHistoryState={clearHistoryState}
        />
      </Suspense>
      {renderActionButtons()}
      {
        <Row>
          <Col>
            <ConfigProvider locale={trTR}>
              <Table
                style={{ width: '100%' }}
                size="small"
                columns={columns}
                dataSource={transactionData}
                pagination={tableParams.pagination}
                loading={loading}
                onChange={handleTableChange}
                rowKey={(record) =>
                  `${record.offerId}_${record?.createdAt || ''}`
                }
                scroll={{
                  y: '70vh',
                }}
              />
            </ConfigProvider>
          </Col>
        </Row>
      }
    </>
  );
};

export default StockTransactions;
